import { T } from 'src/shared/helpers';
import { isRealMesh } from './objectEditorConfig';
import SelectionInspector from './selectionInspector';
import { EmptyNodeIcon, MeshNodeIcon } from '../../../../../widgets/icons/scene';
import { SceneEditor } from '@zakeke/zakeke-3d-core';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DAMDialog from 'src/components/pages/preview3D/dialogs/damDialog';
import { UploadDialog } from 'src/components/pages/preview3D/dialogs/uploadDialog';
import useMeshSides from 'src/components/pages/preview3D/hooks/useMeshSide';
import usePreview3DValidation from 'src/components/pages/preview3D/hooks/usePreview3DValidation';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { getInvalidSceneUploadFileSelectionMessage, SUPPORTED_SCENE_FILE_UPLOAD_EXTENSIONS } from 'src/shared/helpers';
import {
    DuplicateIcon as CloneIcon,
    GroupIcon,
    PlusIcon as ImportIcon,
    MeshNodeIcon as ModelIcon,
    TrashIcon as RemoveIcon,
    SelectedIcon as SelectAllIcon,
    UploadIcon
} from '../../../../../widgets/icons/scene';
import { TreeComponent, TreeHandle, useTreeNodeManager } from '../tree';
import { readMaterialPreview } from './materialPreview';
import ObjectOverlayComponent from './overlay/ObjectOverlayComponent';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import SectionPanel from './sectionPanel';
import { useRemovalPrompt } from './confirmRemoval';
import { SceneEditorAction } from './sceneEditorActionBar';

export interface SceneObjectNode {
    id: string;
    name: string;
    position: [number, number, number];
    rotation: [number, number, number];
    scaling: [number, number, number];
    materialOptions: { value: string; label: string; previewUrl?: string }[];
    selectedMaterial: { value: string; label: string };
    showInAR: boolean;
    enableInVTO: boolean;
    children: SceneObjectNode[];
    visible?: boolean;
    displayOrder?: number;
    metadata?: { [key: string]: any }; // Metadata personalizzati per il nodo
    data: any; // Per ObjectEditor conterrà la mesh di Babylon
    onNodeClick: () => void; // Handler per la selezione mesh quando si clicca sul nodo
}

interface ObjectEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    materials: any[];
    sceneEditor: SceneEditor;
    onMeshUpdate: (meshId: string, updates: Partial<SceneObjectNode>) => void;
    onMeshesUpdated: (meshes: any[]) => void;
    notifySceneEditorUpdates: () => void;
    onRefreshMeshVisibility?: (refreshFn: () => void) => void; // Callback per esporre la funzione di refresh
    onInspectorChange?: (component: React.ReactNode | null) => void;
    /** Porta la colonna di sinistra sui materiali e accende quello dell'oggetto aperto. */
    onRevealMaterial?: (materialId: string) => void;
}

const ObjectEditor: FC<ObjectEditorProps> = ({
    treeRef,
    onMeshUpdate,
    onMeshesUpdated,
    sceneEditor,
    notifySceneEditorUpdates,
    onRefreshMeshVisibility,
    onInspectorChange,
    onRevealMaterial
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const preview3DStore = useContext(Preview3DStoreContext);
    const validate = usePreview3DValidation();
    const { createMeshSide } = useMeshSides();
    const [zkProduct, designManager, sceneManager, zkScene] = usePreview3DStore((x) => [x.zkProduct, x.designManager, x.sceneManager, x.zkScene]);

    // Stato per forzare il refresh delle mesh
    const [meshRefreshTrigger, setMeshRefreshTrigger] = useState(false);
    const [meshes, setMeshes] = useState<any[]>(() => {
        const initialMeshes = sceneEditor?.getSceneMeshes() || [];
        return initialMeshes;
    });

    useEffect(() => {
        if (meshRefreshTrigger && sceneEditor) {
            const newMeshes = sceneEditor.getSceneMeshes();
            setMeshes(newMeshes);
            setMeshRefreshTrigger(false);
        }
    }, [meshRefreshTrigger, sceneEditor]);

    // Esponi la funzione di refresh tramite il callback
    useEffect(() => {
        if (onRefreshMeshVisibility) {
            const refreshFunction = () => {
                setMeshRefreshTrigger(true);
            };
            onRefreshMeshVisibility(refreshFunction);
        }
    }, [onRefreshMeshVisibility]);

    // Controlla periodicamente i cambiamenti nelle mesh per le VTO reference
    useEffect(() => {
        if (!sceneEditor) return;

        const checkMeshChanges = () => {
            const currentMeshes = sceneEditor.getSceneMeshes();
            const currentMeshCount = currentMeshes.length;
            const currentVTOMeshCount = currentMeshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Confronta con lo stato precedente
            const previousMeshCount = meshes.length;
            const previousVTOMeshCount = meshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Se il numero di mesh è cambiato o se le VTO reference sono cambiate
            if (currentMeshCount !== previousMeshCount || currentVTOMeshCount !== previousVTOMeshCount) {
                setMeshRefreshTrigger(true);
            }
        };

        // Controlla ogni 500ms per cambiamenti
        const interval = setInterval(checkMeshChanges, 500);

        return () => clearInterval(interval);
    }, [sceneEditor, meshes]);

    const materials = useMemo(() => {
        if (!sceneEditor) return [];
        return sceneEditor.getAllMaterials();
    }, [sceneEditor]);

    // Funzione per costruire la gerarchia delle mesh
    const buildMeshHierarchy = useCallback(
        (meshes: any[]): SceneObjectNode[] => {
            if (!meshes || meshes.length === 0) {
                return [];
            }

            const meshMap = new Map<string, any>();
            const rootMeshes: any[] = [];

            // Prima passata: crea la mappa delle mesh
            meshes.forEach((mesh) => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;
                meshMap.set(meshId, mesh);
            });

            // Seconda passata: identifica le mesh root
            meshes.forEach((mesh) => {
                const hasParent = mesh.parent && meshMap.has(mesh.parent.uniqueId || mesh.parent.id);
                if (!hasParent) {
                    rootMeshes.push(mesh);
                }
            });

            // L'ELENCO DEI MATERIALI È DELLA SCENA, NON DELLA MESH: sono gli stessi per tutte, e
            // costruirlo dentro la ricorsione voleva dire rifarlo una volta per mesh — con la
            // lettura dell'anteprima ripetuta altrettante volte. Qui si costruisce una volta e lo
            // portano tutte.
            //
            // LA FACCIA DI UN MATERIALE È LA SUA FOTOGRAFIA, quando c'è: fra «Inner.007» e
            // «Other.006» non sceglie nessuno leggendo. Si legge dai metadata della scena — è un
            // accesso a un oggetto, non uno scatto — e chi la fotografia non ce l'ha resta col
            // cerchio vuoto, che è diverso dal non esserci.
            const materialOptions = materials.map((mat: any) => ({
                value: mat.id || mat.name || 'default',
                label: mat.name || 'Default Material',
                previewUrl: readMaterialPreview(sceneEditor, mat.id) ?? undefined
            }));

            // Funzione ricorsiva per convertire mesh in SceneObjectNode
            const convertMeshToNode = (mesh: any): SceneObjectNode => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;

                // Trova i figli di questa mesh
                const children = meshes.filter((m) => m.parent && (m.parent.uniqueId === meshId || m.parent.id === meshId)).map(convertMeshToNode);

                const meshMaterials = materialOptions;

                // Trova il materiale selezionato per questa mesh
                let selectedMaterial = meshMaterials[0];
                if (mesh.material) {
                    const found = meshMaterials.find((mat: any) => mat.value === mesh.material.id || mat.value === mesh.material.name);
                    if (found) {
                        selectedMaterial = found;
                    }
                }

                // Determina se è un nodo root (senza parent tra le mesh)
                const isRootNode = !mesh.parent || !meshes.some((m) => m.uniqueId === mesh.parent.uniqueId || m.id === mesh.parent.id);

                // Determina se è un nodo transform (contiene "transform" nel nome)
                const isTransformNode = (mesh.name || '').toLowerCase().includes('transform');

                // Inizializza metadata dal mesh esistente o crea nuovo
                const baseMetadata = mesh.metadata || {};

                // Aggiungi metadata automatici
                const metadata = {
                    ...baseMetadata,
                    displayOrder: baseMetadata.displayOrder || 0,
                    ...(isRootNode && { isRootNode: true }),
                    ...(isTransformNode && { isTransformNode: true })
                };

                // Determina la visibilità del nodo basandosi sul parent se presente
                let meshVisibility: boolean;

                if (mesh.isVisible === false) {
                    // Se la mesh è esplicitamente false, forza a false
                    meshVisibility = false;
                } else if (mesh.parent && mesh.parent.isVisible !== undefined) {
                    // Se ha un parent, usa la visibilità del parent
                    meshVisibility = mesh.parent.isVisible !== false;
                } else {
                    // Se non ha parent, usa la visibilità della mesh stessa
                    meshVisibility = mesh.isVisible !== false;
                }

                const node: SceneObjectNode = {
                    id: meshId,
                    name: mesh.name || 'Unnamed Mesh',
                    position: mesh.position ? [mesh.position.x, mesh.position.y, mesh.position.z] : [0, 0, 0],
                    rotation: mesh.rotation ? [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z] : [0, 0, 0],
                    scaling: mesh.scaling ? [mesh.scaling.x, mesh.scaling.y, mesh.scaling.z] : [1, 1, 1],

                    materialOptions: meshMaterials,
                    selectedMaterial,
                    // `|| true` rendeva «Show in AR» acceso SEMPRE, qualunque cosa dicesse la mesh: un
                    // interruttore che non si può spegnere non è un interruttore. Si legge e basta,
                    // come faceva il vecchio backoffice.
                    showInAR: sceneEditor.isAREnabledForMesh(mesh.id),
                    enableInVTO: sceneEditor.isTryOnEnabledForMesh(mesh.id),
                    children,
                    data: mesh,
                    visible: meshVisibility,
                    displayOrder: metadata.displayOrder,
                    metadata, // Aggiungi metadata al nodo
                    // Handler per la selezione mesh quando si clicca sul nodo
                    onNodeClick: () => {
                        if (!sceneEditor) {
                            console.warn('⚠️ SceneEditor not available');
                            return;
                        }

                        try {
                            let targetMesh = mesh;
                            const targetMeshId = targetMesh.id || targetMesh.uniqueId;

                            // Attiva l'highlight
                            if (sceneEditor.setHighlightOver) {
                                sceneEditor.setHighlightOver(true);
                            }

                            // Seleziona la mesh
                            if (sceneEditor.selectMesh) {
                                sceneEditor.selectMesh(targetMeshId);
                            }

                            // Attiva la modalità selezione se non è già attiva
                            if (sceneEditor.isSelectionMode !== undefined && !sceneEditor.isSelectionMode) {
                                sceneEditor.isSelectionMode = true;
                            }
                        } catch (error) {
                            console.error('❌ Error selecting mesh from node click:', error);
                        }
                    }
                };

                // Sincronizza anche il metadata sulla mesh di Babylon
                if (!mesh.metadata) mesh.metadata = {};
                Object.assign(mesh.metadata, metadata);

                if (isRootNode || isTransformNode) {
                }

                return node;
            };

            return rootMeshes.map(convertMeshToNode);
        },
        [materials, sceneEditor, meshes]
    );

    // Converti le mesh in SceneObjectNode con logging di riepilogo
    const initialObjects: SceneObjectNode[] = useMemo(() => {
        if (!meshes || meshes.length === 0) {
            return [];
        }

        const result = buildMeshHierarchy(meshes);

        // Riepilogo metadata applicati
        const countMetadata = (nodes: SceneObjectNode[]): { root: number; transform: number; total: number } => {
            let root = 0,
                transform = 0,
                total = 0;

            const count = (nodeList: SceneObjectNode[]) => {
                nodeList.forEach((node) => {
                    total++;
                    if (node.metadata?.isRootNode) root++;
                    if (node.metadata?.isTransformNode) transform++;
                    if (node.children?.length) count(node.children);
                });
            };

            count(nodes);
            return { root, transform, total };
        };

        const stats = countMetadata(result);

        return result;
    }, [meshes, buildMeshHierarchy]);

    const syncNodeToMeshRef = useRef<((node: SceneObjectNode) => void) | undefined>(undefined);

    useEffect(() => {
        syncNodeToMeshRef.current = (node: SceneObjectNode) => {
            if (!node.data) return;

            const mesh = node.data;

            // Sincronizza nome
            mesh.name = node.name;

            // Sincronizza posizione
            if (mesh.position) {
                mesh.position.x = node.position[0];
                mesh.position.y = node.position[1];
                mesh.position.z = node.position[2];
            }

            // Sincronizza rotazione
            if (mesh.rotation) {
                mesh.rotation.x = node.rotation[0];
                mesh.rotation.y = node.rotation[1];
                mesh.rotation.z = node.rotation[2];
            }

            // Sincronizza scala
            if (mesh.scaling) {
                mesh.scaling.x = node.scaling[0];
                mesh.scaling.y = node.scaling[1];
                mesh.scaling.z = node.scaling[2];
            }

            // Sincronizza materiale
            if (node.selectedMaterial && sceneEditor) {
                try {
                    const material = materials.find((mat) => mat.id === node.selectedMaterial.value || mat.name === node.selectedMaterial.value);
                    if (material && mesh.material !== material) {
                        mesh.material = material;
                    }
                } catch (error) {
                    console.error('Error applying material:', error);
                }
            }

            // Sincronizza visibilità
            if (mesh.setEnabled) {
                mesh.setEnabled(node.visible !== false);
            } else if (mesh.isVisible !== undefined) {
                mesh.isVisible = node.visible !== false;
            }

            // Sincronizza metadata
            if (!mesh.metadata) mesh.metadata = {};
            mesh.metadata.displayOrder = node.displayOrder;

            // Sincronizza metadata personalizzati
            if (node.metadata) {
                Object.assign(mesh.metadata, node.metadata);
            }

            // AR e VTO NON si scrivono qui: li scrive il gesto che li cambia, in `objectEditorConfig`.
            // Passare di qui vorrebbe dire riscriverli a ogni spostamento di un nodo, e i metodi del
            // motore scendono nei figli — una modifica qualunque ristamperebbe la scelta su tutta la
            // discendenza.

            if (onMeshUpdate) {
                onMeshUpdate(node.id, node);
            }

            // E ADESSO SI RIDISEGNA. Il palco non dipinge di continuo: disegna quando qualcuno
            // dichiara che c'è qualcosa di nuovo da vedere — è così che una scena 3D ferma non
            // consuma la batteria. Scrivere `mesh.position` cambia il modello e non la fotografia:
            // finché nessuno chiede il fotogramma, a schermo resta quello di prima, e la modifica
            // compariva solo alla mossa successiva — che la faceva chiedere per conto suo. Da fuori
            // era «tiro lo slider e non succede niente, poi tutto insieme»: rotto, ma in ritardo.
            //
            // È lo stesso gesto che fanno già le luci (`render`) e il VTO (`setRenderingNeeded`).
            sceneEditor?.setRenderingNeeded?.();
        };
    }, [onMeshUpdate, materials, sceneEditor]);

    // «DOVE STA?» — la freccetta della testata non cambia niente: porta la riga in vista e la fa
    // lampeggiare. Passa di qui e non dal manico dell'albero perché quel manico è uno solo per
    // quattro sezioni: una chiamata imperativa poteva finire su un altro albero e non fare niente,
    // in silenzio. Il gettone serve a poter chiedere due volte la stessa riga.
    const [blinkRequest, setBlinkRequest] = useState<{ nodeId: string; token: number } | null>(null);

    const askWhereItIs = useCallback((nodeId: string) => setBlinkRequest((previous) => ({ nodeId, token: (previous?.token ?? 0) + 1 })), []);

    const createObjectOverlayById = useCallback(
        (nodeId: string): (() => React.ReactElement) => {
            return () => {
                // Trova il nodo aggiornato al momento del render dell'overlay
                const { findNodeById } = treeNodeManagerRef.current || {};
                const node = findNodeById ? findNodeById(nodeId) : null;

                if (!node) {
                    console.error('Nodo non trovato per overlay:', nodeId);
                    return <div>Node not found</div>;
                }

                return (
                    <ObjectOverlayComponent
                        key={`overlay-component-${node.id}`}
                        node={node}
                        sceneEditor={sceneEditor}
                        onRevealMaterial={onRevealMaterial}
                        onReveal={() => askWhereItIs(node.id)}
                        onPropertiesUpdate={(updates) => {
                            const { updateNode, findNodeById } = treeNodeManagerRef.current || {};

                            if (!updateNode || !findNodeById) return;

                            // Aggiorna il nodo nel tree
                            updateNode(node.id, updates);

                            // Trova il nodo aggiornato e sincronizza con Babylon
                            const updatedNode = findNodeById(node.id);
                            if (updatedNode && syncNodeToMeshRef.current) {
                                const nodeWithUpdates = { ...updatedNode, ...updates };
                                syncNodeToMeshRef.current(nodeWithUpdates);
                            }

                            // Gestione speciale per i materiali
                            if ('selectedMaterial' in updates && sceneEditor) {
                                // SVUOTARE LO SLOT E TOGLIERE L'ASSEGNAZIONE, non cancellare il materiale:
                                // quello resta nella sua libreria e su ogni altra mesh che lo porta. Qui la
                                // mesh smette di averne uno, lei e i suoi figli.
                                if (!updates.selectedMaterial?.value) {
                                    const clearRecursively = (meshNode: SceneObjectNode) => {
                                        if (meshNode.data) meshNode.data.material = null;
                                        meshNode.children?.forEach(clearRecursively);
                                    };

                                    const toClear = findNodeById(node.id);
                                    if (toClear) clearRecursively(toClear);
                                    notifySceneEditorUpdates();
                                    return;
                                }

                                const material = sceneEditor.getMaterialByID(updates.selectedMaterial!.value);
                                if (material) {
                                    // Funzione ricorsiva per applicare il materiale a mesh e figli
                                    const applyMaterialRecursively = (meshNode: SceneObjectNode) => {
                                        sceneEditor.applyMaterial(material, meshNode.data);
                                        meshNode.children?.forEach(applyMaterialRecursively);
                                    };

                                    const currentNodeForMaterial = findNodeById(node.id);
                                    if (currentNodeForMaterial) {
                                        applyMaterialRecursively(currentNodeForMaterial);
                                    }
                                }

                                // Aggiorna anche i nodi figli con il nuovo selectedMaterial
                                const updateChildrenMaterial = (meshNode: SceneObjectNode) => {
                                    meshNode.children?.forEach((child) => {
                                        updateNode(child.id, { selectedMaterial: updates.selectedMaterial });
                                        updateChildrenMaterial(child);
                                    });
                                };

                                const currentNodeForChildren = findNodeById(node.id);
                                if (currentNodeForChildren) {
                                    updateChildrenMaterial(currentNodeForChildren);
                                }
                            }
                        }}
                        onPropertyChanged={(property, value) => {
                            // Notifica gli aggiornamenti del scene editor se necessario
                            if (notifySceneEditorUpdates) {
                                notifySceneEditorUpdates();
                            }
                        }}
                    />
                );
            };
        },
        [meshes, materials]
    );

    // Wrapper per compatibilità con l'hook che si aspetta (node: T) => () => React.ReactNode
    const createObjectOverlay = useCallback(
        (node: SceneObjectNode): (() => React.ReactElement) => {
            // Semplicemente delega alla versione che prende l'ID
            return createObjectOverlayById(node.id);
        },
        [createObjectOverlayById]
    );

    // Ref per memorizzare le funzioni dell'hook dopo l'inizializzazione
    const treeNodeManagerRef = useRef<
        | {
              updateNode: (id: string, updates: Partial<SceneObjectNode>) => void;
              findNodeById: (id: string) => SceneObjectNode | undefined;
          }
        | undefined
    >(undefined);

    // Usa l'hook per la gestione dei nodi con il createObjectOverlay già definito
    const { filter, setFilter, selectedIds, filteredData, updateNode, findNodeById, removeNodes, cloneNodes, treeState, syncInitialVisibilityFromMeshes } =
        useTreeNodeManager<SceneObjectNode>(initialObjects, createObjectOverlay);

    // IL RIFERIMENTO SI AGGIORNA DURANTE IL RENDER, non dopo. Il pannello di un oggetto non
    // tiene dentro il nodo: lo ricerca da qui ogni volta che lo si disegna. Aggiornato dentro un
    // effetto restava indietro di un giro, e gli effetti del figlio girano prima di quelli del
    // padre: l'albero chiedeva un pannello nuovo e se lo vedeva costruire ancora sui dati
    // vecchi, cioè col nome di prima.
    treeNodeManagerRef.current = {
        updateNode,
        findNodeById
    };

    // 🔧 NUOVO: Sincronizza la visibilità iniziale delle mesh solo UNA volta quando i nodi sono pronti
    const [initialSyncDone, setInitialSyncDone] = useState(false);

    useEffect(() => {
        if (initialObjects.length > 0 && !initialSyncDone) {
            syncInitialVisibilityFromMeshes();
            setInitialSyncDone(true);
        }
    }, [initialObjects, syncInitialVisibilityFromMeshes, initialSyncDone]);

    const prepareModel = (meshes: BABYLON.AbstractMesh[]) => {
        const { amountOfSides, amountOfMeshesWithGeometry } = validate();

        if (amountOfSides === 1 && amountOfMeshesWithGeometry === 1) {
            const firstMeshWithGeometry = sceneEditor!.scene!.meshes.find((x) => x.getTotalIndices() > 0);
            if (firstMeshWithGeometry) {
                const meshSide = createMeshSide(zkProduct!.get('colors').at(0).get('colorID'), zkProduct!.get('colors').at(0).get('sides').at(0).get('sideID'));
                meshSide.set('meshID', firstMeshWithGeometry.id);
            }
        }

        // Aggiorna le mesh nell'ObjectEditor dopo l'upload
        if (sceneEditor) {
            setMeshRefreshTrigger(true);
            const updatedMeshes = sceneEditor.getSceneMeshes();
            onMeshesUpdated(updatedMeshes);
        }
    };

    const handleUploadClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = SUPPORTED_SCENE_FILE_UPLOAD_EXTENSIONS;
        input.multiple = true;

        input.onchange = (e) => {
            document.body.removeChild(input);
            const files = (e.target as HTMLInputElement).files;
            if (files) {
                const validationMessage = getInvalidSceneUploadFileSelectionMessage(files);
                if (validationMessage) {
                    showErrorNotification({ message: validationMessage });
                    return;
                }

                showDialog(
                    'upload-file',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <UploadDialog
                            files={files}
                            onComplete={(meshes) => {
                                prepareModel(meshes);
                                closeDialog('upload-file');
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
            }
        };

        input.oncancel = () => {
            document.body.removeChild(input);
        };

        input.style.display = 'none';
        input.click();
        document.body.appendChild(input);
    };

    const handleUploadFromDAMClick = () => {
        showDialog(
            'dam-dialog',
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <DAMDialog
                    onClose={(productAssetUrl: string) => {
                        closeDialog('dam-dialog');
                        showDialog(
                            'upload-file',
                            <Preview3DStoreContext.Provider value={preview3DStore}>
                                <UploadDialog
                                    damProductAssetUrl={productAssetUrl}
                                    onComplete={(meshes) => {
                                        prepareModel(meshes);
                                        closeDialog('upload-file');
                                    }}
                                />
                            </Preview3DStoreContext.Provider>
                        );
                    }}
                />
            </Preview3DStoreContext.Provider>
        );
    };

    // ARRIVA LO STATO VOLUTO, NON QUELLO ATTUALE. L'albero chiama con la visibilità che
    // l'elemento deve AVERE dopo il clic; qui la si negava di nuovo, e in 3D la mesh faceva
    // l'opposto di quello che diceva l'occhio.
    //
    // L'albero ha già aggiornato il proprio stato prima di chiamare: qui si tocca solo
    // Babylon. Aggiornarlo di nuovo — `updateNode` più `toggleVisibility` — era un secondo
    // ribaltamento che annullava il primo.
    const handleToggleNodeVisibility = useCallback(
        (nodeId: string, nextVisible: boolean) => {
            if (!sceneEditor) return;

            // L'albero ragiona per id di nodo, Babylon per id di mesh: i due non coincidono.
            const targetNode = findNodeById(nodeId);
            const meshId = targetNode?.data?.id ?? nodeId;

            sceneEditor.showHideMesh(meshId, nextVisible, false);

            // Riaccendendo un contenitore si riaccendono i suoi discendenti: spegnendolo li
            // aveva spenti Babylon per gerarchia, e senza questo resterebbero invisibili.
            if (nextVisible && targetNode?.children?.length) {
                const showChildren = (children: SceneObjectNode[]) => {
                    children.forEach((child) => {
                        if (child.data?.id) sceneEditor.showHideMesh(child.data.id, true, false);
                        if (child.children?.length) showChildren(child.children);
                    });
                };

                showChildren(targetNode.children);
            }
        },
        [sceneEditor, findNodeById]
    );

    // Trasforma i nodi in formato compatibile con TreeComponent
    const transformNodesForTree = useCallback(
        (nodes: SceneObjectNode[]): any[] => {
            if (!nodes || nodes.length === 0) {
                return [];
            }

            return nodes.map((node) => ({
                id: node.id,
                name: node.name,
                visible: node.visible,
                displayOrder: node.displayOrder,
                // COSA È IL NODO, non se ha figli. L'albero sceglieva il glifo da
                // «ha figli», e così una mesh vera con dei figli prendeva il ramo mentre un
                // nodo di trasformazione senza figli prendeva il cubo: il segno diceva la
                // posizione nell'albero invece della natura della cosa.
                // La geometria è il discrimine: una mesh vera ha vertici, un nodo di
                // trasformazione è un'origine e basta. Il nome e i metadata sono il ripiego
                // per quando il modello di Babylon non è a portata.
                kind: isRealMesh(node) ? 'mesh' : 'group',
                children: node.children ? transformNodesForTree(node.children) : [],
                component: createObjectOverlay(node), // Usa il wrapper che prende il nodo
                doNotOpenOverlay: node.name && node.name.toLowerCase().includes('vto_reference'), // Don't open overlay for VTO reference meshes
                customIcon: node.name && node.name.toLowerCase().includes('vto_reference') ? '🎭' : undefined, // Custom icon for VTO reference meshes
                data: node.data,
                onNodeClick: node.onNodeClick // ✅ AGGIUNTO: Passa l'onNodeClick al nodo trasformato
            }));
        },
        [createObjectOverlay]
    );

    // 5. Ottimizza la memoizzazione dei treeNodes
    const treeNodes = useMemo(() => {
        // Usa filteredData se c'è un filtro, altrimenti usa treeState.nodes
        const sourceNodes = filter ? filteredData : treeState.nodes;
        const result = transformNodesForTree(sourceNodes);
        return result;
    }, [treeState.nodes, filteredData, filter, transformNodesForTree]);

    // ✅ NUOVO: Funzione per trovare un nodo basandosi sui dati della mesh
    const findNodeByDataID = useCallback((nodes: SceneObjectNode[], dataID: string): SceneObjectNode | null => {
        for (const node of nodes) {
            // Controlla se questo nodo corrisponde
            if (node.data?.uniqueId === dataID || node.data?.id === dataID || node.id === dataID) {
                return node;
            }
            // Cerca ricorsivamente nei figli
            if (node.children && node.children.length > 0) {
                const found = findNodeByDataID(node.children, dataID);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }, []);

    // Override del treeState per fornire i nodi trasformati
    const treeStateForComponent = useMemo(() => {
        const result = {
            ...treeState,
            nodes: treeNodes,
            findNodeByDataID: (nodes: any[], dataID: string) => findNodeByDataID(nodes, dataID)
        };
        return result;
    }, [treeState, treeNodes, findNodeByDataID]);

    // Struttura dati standard per nuovi oggetti
    // «Seleziona tutto» sta fra i comandi, non sopra l'albero. Come casella era l'unico
    // controllo di quel genere nella colonna e sfalsava l'allineamento con le altre sezioni;
    // qui è un comando fra comandi. E siccome accende e spegne, resta premuto finché tutto
    // il visibile è selezionato.
    const selectableIds: string[] = treeState.flatVisibleList.map((node: any) => node.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => treeState.selectedIds.has(id));

    // NELLA BARRA RESTA CIÒ CHE NON HA UN SOGGETTO. «Add object» porta dentro qualcosa che ancora
    // non esiste e «Select all» agisce sull'elenco intero: nessuno dei due appartiene a una riga.
    // Tutti gli altri sono scesi sulla riga, nel suo «…», dove il soggetto è la riga stessa —
    // prima bisognava sceglierla e poi risalire alla barra, con il comando lontano dalla cosa.
    // Una sola conferma per togliere, in tutte le liste: il testo lo compone lei.
    const askRemove = useRemovalPrompt();

    const tools: SceneEditorAction[] = [
        {
            name: T._('Add object', 'Admin'),
            tooltip: T._('A 3D model into this scene', 'Admin'),
            icon: <ImportIcon />,
            kind: 'add',
            // DA DOVE ARRIVA IL MODELLO LO DICE CHI CLICCA, e lo dice sul posto. Prima lo chiedeva
            // una finestra — «Model Upload source», due bottoni e una domanda — cioè un modulo da
            // compilare prima che la cosa esista: due clic e un cambio di contesto per una scelta
            // fra due. Qui è un menu sotto il bottone, e i due modi restano gli stessi di prima.
            //
            // `onClick` non viene mai chiamato finché il menu ha voci: è il ripiego se restasse
            // vuoto.
            onClick: () => handleUploadClick(),
            menu: [
                {
                    name: T._('Upload 3D Model', 'Admin'),
                    tooltip: T._('From a file on this computer', 'Admin'),
                    icon: <UploadIcon />,
                    onClick: handleUploadClick
                },
                {
                    name: T._('Import from 3D DAM', 'Admin'),
                    tooltip: T._('From the asset library', 'Admin'),
                    icon: <ModelIcon />,
                    onClick: handleUploadFromDAMClick
                }
            ]
        },
        {
            name: allSelected ? T._('Deselect all', 'Admin') : T._('Select all', 'Admin'),
            tooltip: T._('Everything visible in this list', 'Admin'),
            icon: <SelectAllIcon />,
            kind: 'action',
            active: allSelected,
            disabled: selectableIds.length === 0,
            onClick: () => treeState.setSelectedIds(new Set(allSelected ? [] : selectableIds))
        }
    ];

    // UN MOTORE, DUE PORTE. Gli stessi comandi arrivano dal «…» di una riga — e allora il soggetto
    // è quella riga — oppure dal pannello del mucchio, e allora sono le righe prese. Chi chiama
    // passa l'elenco; qui dentro la selezione non si guarda mai, perché il menu di una riga NON
    // selezionata deve cadere su di lei e non altrove.
    //
    // «Rename» compare solo quando il soggetto è uno: dare un nome solo a otto oggetti insieme non
    // vuol dire niente.
    const subjectCommands = useCallback(
        (ids: string[]): SceneEditorAction[] => {
            if (!ids.length) return [];

            const commands: SceneEditorAction[] = [
                {
                    name: T._('Group', 'Admin'),
                    tooltip: T._('In a new Empty', 'Admin'),
                    icon: <GroupIcon />,
                    kind: 'action',
                    onClick: () => {
                        treeState.closeOverlay();
                        const formData = { name: '' };

                        showDialog(
                            'group-dialog',
                            <Dialog
                                title={T._('New group', 'Admin')}
                                buttons={[
                                    {
                                        label: T._('OK', 'Admin'),
                                        onClick: () => {
                                            const val = formData.name.trim();
                                            if (!val) return;

                                            // LA CARTELLA È UN EMPTY VERO, non una riga in più nell'albero. Prima
                                            // il gruppo nasceva solo qui dentro: l'albero però si ricostruisce dalle
                                            // mesh della scena a ogni aggiornamento — basta un duplica, un import, un
                                            // remove — e una cartella che in Babylon non esiste sparisce senza dire
                                            // niente. `createGroup` crea il nodo di trasformazione che la regge, e da
                                            // lì in poi la cartella c'è anche dopo il salvataggio.
                                            const meshesToGroup = ids.map((id) => findNodeById(id)?.data).filter(Boolean);

                                            if (!meshesToGroup.length || !sceneEditor?.createGroup) {
                                                closeDialog('group-dialog');
                                                return;
                                            }

                                            // Il gruppo nasce dove stanno le cose: se hanno tutte lo stesso padre,
                                            // dentro quello; se vengono da rami diversi, alla radice.
                                            const parents = new Set<string | null>(meshesToGroup.map((mesh: any) => mesh.parent?.id ?? null));
                                            const parentId = parents.size === 1 ? [...parents][0] : null;

                                            const created = sceneEditor.createGroup(parentId, val, meshesToGroup as any);

                                            if (!created) {
                                                showErrorNotification({ message: T._('The group could not be created.', 'Admin') });
                                                closeDialog('group-dialog');
                                                return;
                                            }

                                            setMeshRefreshTrigger(true);
                                            onMeshesUpdated(sceneEditor.getSceneMeshes());
                                            notifySceneEditorUpdates();
                                            closeDialog('group-dialog');
                                        }
                                    }
                                ]}
                            >
                                <FormControl label={T._('Group name', 'Admin')}>
                                    <Input
                                        placeholder={T._('Type the group name...', 'Admin')}
                                        fullWidth
                                        onChange={(e) => {
                                            formData.name = e.target.value;
                                        }}
                                    />
                                </FormControl>
                            </Dialog>
                        );
                    }
                },
                {
                    name: T._('Duplicate', 'Admin'),
                    tooltip: T._('A copy in this scene', 'Admin'),
                    icon: <CloneIcon />,
                    kind: 'action',
                    onClick: () => {
                        treeState.closeOverlay();

                        // Raccogli i nodi da clonare
                        const nodesToClone = ids.map((id) => findNodeById(id)).filter(Boolean);

                        // Clona prima le mesh di Babylon
                        const clonedMeshes = new Map<string, any>();
                        nodesToClone.forEach((node) => {
                            if (node && node.data && sceneEditor) {
                                try {
                                    const clonedMesh = sceneEditor.cloneMesh(node.data.id || node.data.uniqueId);
                                    clonedMeshes.set(node.id, clonedMesh);
                                } catch (error) {
                                    console.error(`Errore clonando mesh per nodo ${node.name}:`, error);
                                }
                            }
                        });

                        // Ora clona i nodi dell'albero e assegna le mesh clonate
                        cloneNodes(ids, 'Copy', (node: SceneObjectNode) => clonedMeshes.get(node.id));

                        // Dopo la clonazione, aggiorna la lista delle mesh per includere quelle nuove
                        if (sceneEditor) {
                            setMeshRefreshTrigger(true);
                            const updatedMeshes = sceneEditor.getSceneMeshes();
                            onMeshesUpdated(updatedMeshes);
                        }

                        // Notifica gli aggiornamenti una sola volta
                        notifySceneEditorUpdates();
                    }
                }
            ];

            commands.push({
                name: T._('Remove', 'Admin'),
                tooltip: T._('Out of this scene', 'Admin'),
                icon: <RemoveIcon />,
                kind: 'danger',
                onClick: () => {
                    treeState.closeOverlay();

                    // IL RAMO CONTA NELLA DOMANDA: togliere un Empty porta via anche i suoi, e chi
                    // conferma deve saperlo PRIMA — non dopo, guardando l'albero più corto.
                    const branchSize = (node: any): number => ((node?.children ?? []) as any[]).reduce((sum, child) => sum + 1 + branchSize(child), 0);

                    // Chi è già dentro un ramo preso non si nomina due volte.
                    const isUnderSelection = (id: string) => {
                        let parent = treeState.findParentId(id);
                        while (parent) {
                            if (ids.includes(parent)) return true;
                            parent = treeState.findParentId(parent);
                        }
                        return false;
                    };

                    const tops = ids
                        .filter((id) => !isUnderSelection(id))
                        .map((id) => findNodeById(id))
                        .filter((node): node is NonNullable<typeof node> => node != null);

                    askRemove({
                        names: tops.map((node) => node.name),
                        one: T._('object', 'Admin'),
                        many: T._('objects', 'Admin'),
                        inside: tops.reduce((sum, node) => sum + branchSize(node), 0),
                        onConfirm: () => {
                            // Fai dispose delle mesh prima di rimuovere i nodi
                            ids.forEach((id) => {
                                const node = findNodeById(id);

                                if (node && node.data && typeof node.data.dispose === 'function') {
                                    try {
                                        node.data.dispose();
                                    } catch (error) {
                                        console.error('Error disposing mesh:', error);
                                    }
                                }
                            });

                            removeNodes(ids);
                            onMeshesUpdated(sceneEditor.getSceneMeshes());
                        }
                    });
                }
            });

            return commands;
        },
        [
            treeState,
            showDialog,
            closeDialog,
            cloneNodes,
            findNodeById,
            sceneEditor,
            setMeshRefreshTrigger,
            onMeshesUpdated,
            notifySceneEditorUpdates,
            updateNode,
            removeNodes,
            askRemove
        ]
    );

    // I COMANDI SONO FUNZIONI NUOVE A OGNI RENDER, e l'effetto qui sotto non può dipenderne:
    // gli basterebbe per ripartire ogni volta, mandando all'Inspector un elemento nuovo che rende
    // di nuovo l'editor, che rifà i comandi. Il giro non si ferma più. L'effetto guarda quindi la
    // SELEZIONE — che cambia solo quando cambia davvero — e i comandi li legge da qui, sempre
    // freschi perché il riferimento si aggiorna a ogni render.
    const subjectCommandsRef = useRef(subjectCommands);
    subjectCommandsRef.current = subjectCommands;

    // CON PIÙ DI UNA RIGA PRESA, L'INSPECTOR GUARDA IL MUCCHIO. L'albero passa il pannello della
    // riga su cui è caduto il clic, e con sette righe scelte quel pannello descriveva una delle
    // sette come se le altre non ci fossero. Qui la selezione ha la precedenza: è la sola che sa
    // di essere al plurale.
    useEffect(() => {
        if (!onInspectorChange || selectedIds.length < 2) return;

        const entries = selectedIds
            .map((id) => findNodeById(id))
            .filter((node): node is NonNullable<typeof node> => node != null)
            .map((node) => ({
                id: node.id,
                name: node.name,
                icon: node.children && node.children.length > 0 ? <EmptyNodeIcon /> : <MeshNodeIcon />
            }));

        onInspectorChange(<SelectionInspector entries={entries} actions={subjectCommandsRef.current(selectedIds)} subjectLabel={T._('objects', 'Admin')} />);
    }, [selectedIds, findNodeById, onInspectorChange]);

    return (
        <SectionPanel actions={tools} filter={filter} onFilterChange={setFilter} searchPlaceholder={T._('Search by name or material...', 'Admin')} multiSelect>
            <TreeComponent
                ref={treeRef}
                blinkRequest={blinkRequest}
                treeState={treeStateForComponent}
                expandAll={!!filter}
                treeLabel='Scene objects'
                showVisibilityIcon={true}
                disableOverlay={!!onInspectorChange}
                onSelectedComponentChange={onInspectorChange}
                /* IL NOME SI CAMBIA DOVE SI LEGGE: si sceglie la riga, e un secondo clic sul nome lo apre.
                   Era una voce nel «…» che apriva una finestra — una porta in più per la stessa stanza, e
                   il mockup quella voce non ce l'ha. IL NOME STA IN DUE POSTI, il nodo e la mesh del
                   motore, e vanno scritti tutti e due o il primo ridisegno riporta su quello di prima. */
                onNodeRename={(node, name) => {
                    updateNode(node.id, { name });

                    const renamed = findNodeById(node.id);
                    if (renamed?.data) renamed.data.name = name;
                }}
                rowActions={(node) => subjectCommands([node.id])}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Aggiorna la gerarchia in Babylon
                    nodesToMove.forEach((id) => {
                        const node = findNodeById(id);
                        if (node && node.data) {
                            const targetNode = targetParentId ? findNodeById(targetParentId) : null;
                            const newParent = targetNode?.data || null; // Usa data anche per il target

                            // Aggiorna il parent nella mesh di Babylon
                            if (node.data.setParent) {
                                node.data.setParent(newParent);
                            } else if (node.data.parent !== undefined) {
                                node.data.parent = newParent;
                            }
                        }
                    });

                    // LO SPOSTAMENTO NELL'ALBERO L'HA GIÀ FATTO CHI HA LASCIATO LA RIGA: qui resta
                    // solo la parte che l'albero non sa fare, cioè riparentare la mesh in Babylon.
                }}
                onToggleNodeVisibility={handleToggleNodeVisibility}
            />
        </SectionPanel>
    );
};

export default ObjectEditor;
